import { TableWidgetCell } from "@/elements/table/tableCell";
import type { TableWidget } from "./table";
import type {
    TableCellRound,
    TableOverAnyCellStatusFalse,
    TableOverAnyCellStatusTrue,
    TableWidgetCellStateFull,
    TableWidgetRowFixState,
    TableWidgetRowStateMap,
} from "./types";

export class TableWidgetRow {
    constructor(cellsAmount = 0, tableWidget: TableWidget) {
        this.tableWidget = tableWidget;

        for (let i = 0; i < cellsAmount; i++) {
            this.cells.push(new TableWidgetCell());
        }
    }

    updateRowHeight(translateY: number | undefined = undefined) {
        let maxHeight = -1;

        for (let i = 0; i < this.cells.length; i++) {
            this.cells[i].textPart.hardWidth =
                this.cells[i].width - 2 * this.cells[i].horizontalPadding;
            this.cells[i].textPart.fitShape();

            const actualMinHeight =
                this.cells[i].textPart.baseHeight + 2 * this.cells[i].verticalPadding;

            if (translateY !== undefined) {
                this.cells[i].height = Math.max(this.cells[i].height + translateY, actualMinHeight);
            } else if (this.cells[i].height < actualMinHeight) {
                this.cells[i].height = actualMinHeight;
            }

            if (this.cells[i].height > maxHeight) {
                maxHeight = this.cells[i].height;
            }
        }

        for (let i = 0; i < this.cells.length; i++) {
            this.cells[i].height = maxHeight;
        }
    }

    updatePosition(startX: number, currY: number, skipColumn = -1) {
        // обновляем startX, startY
        let currX = startX;

        for (let i = 0; i < this.cells.length; i++) {
            if (i !== skipColumn) {
                this.cells[i].updatePosition(currX, currY);
            }

            currX += this.cells[i].width;
        }
    }

    getWidth() {
        let width = 0;

        for (let i = 0; i < this.cells.length; i++) {
            width += this.cells[i].width;
        }

        return width;
    }

    getHeight() {
        const height = 0;

        if (this.cells.length !== 0) {
            return this.cells[0].height;
        }

        return height;
    }

    getSectionCount() {
        let rowSectionCount = 0;

        for (let i = 0; i < this.cells.length; i++) {
            rowSectionCount += this.cells[i].sectionWidth;
        }

        return rowSectionCount;
    }

    getCellByColumnIndex(columnIndex: number) {
        let correctIndexCell = 0;
        let targetColumn = 0;

        for (let i = 0; i < this.cells.length; i++) {
            if (targetColumn === columnIndex) {
                correctIndexCell = i;
                break;
            }
            targetColumn += this.cells[i].sectionWidth;
        }

        return this.cells[correctIndexCell];
    }

    getIndexCellByColumnIndex(columnIndex: number) {
        let correctIndexCell = 0;
        let targetColumn = 0;

        for (let i = 0; i < this.cells.length; i++) {
            if (targetColumn === columnIndex) {
                correctIndexCell = i;
                break;
            }
            targetColumn += this.cells[i].sectionWidth;
        }

        return correctIndexCell;
    }

    getColumnCount() {
        return this.cells.reduce((prev, curr) => prev + curr.sectionWidth, 0);
    }

    deleteCellByByColumnIndex(columnIndex: number) {
        const indexCell = this.getIndexCellByColumnIndex(columnIndex);

        this.cells.splice(indexCell, 1);
    }

    isOverAnyCell(): Omit<TableOverAnyCellStatusTrue, "row"> | TableOverAnyCellStatusFalse {
        let column = 0;

        for (let i = 0; i < this.cells.length; i++) {
            if (this.cells[i].isOver()) {
                return {
                    status: true,
                    column: column,
                    sectionWidth: this.cells[i].sectionWidth,
                };
            }
            column += this.cells[i].sectionWidth;
        }

        return { status: false };
    }

    resize(x: number, y: number) {
        const rowWidth = this.getWidth();

        for (let i = 0; i < this.cells.length; i++) {
            const diffX = x * (this.cells[i].width / rowWidth);

            if (this.cells[i].width + diffX < 0) return;

            this.cells[i].width += diffX;
            this.cells[i].height += y;
        }
    }

    draw(roundTop = false, roundBottom = false, skipColumn = -1) {
        let correctSkipColumnIndex = skipColumn;

        if (skipColumn !== -1) {
            let correctIndexCell = 0;
            let targetColumn = 0;

            for (let j = 0; j < this.cells.length; j++) {
                if (targetColumn === skipColumn) {
                    correctIndexCell = j;
                    break;
                }
                targetColumn += this.cells[j].sectionWidth;
            }

            correctSkipColumnIndex = correctIndexCell;
        }
        for (let i = 0; i < this.cells.length; i++) {
            if (i === correctSkipColumnIndex) continue;

            const cellRound: TableCellRound = {
                "0": 0,
                "1": 0,
                "2": 0,
                "3": 0,
            };

            if (roundTop) {
                if (i === 0) {
                    cellRound["0"] = 10;
                }

                if (i === this.cells.length - 1) {
                    cellRound["1"] = 10;
                }
            }

            if (roundBottom) {
                if (i === 0) {
                    cellRound["3"] = 10;
                }

                if (i === this.cells.length - 1) {
                    cellRound["2"] = 10;
                }
            }

            this.cells[i].draw(cellRound);
        }
    }

    getCellsStates() {
        const states: TableWidgetCellStateFull[] = [];

        for (let i = 0; i < this.cells.length; i++) {
            states.push(this.cells[i].getState("full"));
        }

        return states;
    }

    loadCellsStates(states: TableWidgetCellStateFull[]) {
        this.cells = [];

        for (let i = 0; i < states.length; i++) {
            const newCell = new TableWidgetCell();
            newCell.loadState(states[i], "full");
            this.cells.push(newCell);
        }
    }

    getState<T extends TableWidgetRowFixState>(fixType: T): TableWidgetRowStateMap[T] {
        switch (fixType) {
            case "full":
                return {
                    cells: this.getCellsStates(),
                } as TableWidgetRowStateMap[T];
            default:
                return {} as never;
        }
    }

    loadState<T extends TableWidgetRowFixState>(stateObj: TableWidgetRowStateMap[T], fixType: T) {
        switch (fixType) {
            case "full":
                this.loadCellsStates(stateObj.cells);
                break;
        }
    }

    cells: TableWidgetCell[] = [];
    tableWidget: TableWidget;
}
